import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { StyledFormElementWrapper } from 'theme/styles';
import { IFormElementProps } from 'models/form';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StyledDatePicker, StyledTextField } from 'components/molecules/FormDatePicker/styles';
import { getDateFromISOOrNull, isValidDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { DateView } from '@mui/x-date-pickers';
import { getNestedErrorMessage } from 'components/molecules/FormInput/helpers';

export enum EFormInputTypes {
  text = 'text',
  password = 'password',
}

interface IFormInput extends IFormElementProps {
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  openTo?: DateView;
  arrayName?: string;
  arrayIndex?: number;
  fieldName?: string;
  dateFormat?: string;
  disabled?: boolean;
  helperText?: string;
  required?: boolean;
}

export const FormDatePicker = ({
  name,
  label,
  control,
  errors,
  minDate,
  maxDate,
  openTo = 'day',
  arrayName,
  arrayIndex,
  fieldName,
  placeholder,
  dateFormat,
  disabled = false,
  helperText,
  required = false,
}: IFormInput) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string>('');
  const { shortDateFormat, getProperties } = useGlobalProperty();

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    let isError = false;
    if (name.includes('.') && !name.includes('[') && Object.keys(errors).length) {
      const keys = name.split('.');
      const errorMessage = getNestedErrorMessage(errors, keys);
      if (keys && errorMessage) {
        isError = true;
        setError(errorMessage);
      }
    } else {
      const errorMessage =
        arrayName && arrayIndex !== undefined && fieldName
          ? errors?.[arrayName]?.[arrayIndex]?.[fieldName]?.message
          : errors[name]?.message;

      isError = true;
      setError(errorMessage);
    }
    if (!isError) {
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, name]);

  const finalDateFormat = dateFormat || shortDateFormat;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledFormElementWrapper disabled={disabled}>
        <FormLabel name={name} label={label} disabled={disabled} required={required} />
        <Controller
          name={name}
          control={control}
          defaultValue={null}
          render={({ field }) => (
            <StyledDatePicker
              openTo={openTo}
              onAccept={(newDate) => {
                if (newDate === null) {
                  field.onChange(null);
                }
              }}
              disabled={disabled}
              format={finalDateFormat}
              value={getDateFromISOOrNull(field.value)}
              minDate={minDate}
              maxDate={maxDate}
              onChange={(e) => {
                if (isValidDate(e) || e === null) {
                  field.onChange(e);
                }
              }}
              slotProps={{
                field: {
                  clearable: true,
                },
                textField: {
                  ...StyledTextField,
                  ref: field.ref,
                  inputProps: {
                    inputMode: 'numeric',
                    placeholder: placeholder || finalDateFormat.toUpperCase(),
                    id: name,
                    disabled: !!disabled,
                  },
                  error: !!error,
                  helperText,
                },
              }}
            />
          )}
        />
        <FormErrorLabel label={error} />
      </StyledFormElementWrapper>
    </LocalizationProvider>
  );
};
